import { Track } from "@/src/lib/tracking";
import { type Spec } from "@json-render/react";
import { WS_PUSH_ALL_CHANNEL } from "@features/api/generalWS/const";
import {
  isValidSpec,
  parseDynamicUiBody,
} from "@screens/notifications/getNotifyMessageSpec";

/**
 * wspush:all 通道下 userData 消息的 data 负载（与后端 Java convertAndSend JSON 对齐，字段可扩展）
 */
interface WSUserDataPushData {
  title?: string;
  description?: string;
  [key: string]: unknown;
}

/**
 * WebSocket NF 解析后的 wspush:all / userData 结构
 */
export interface WSUserDataPushMessage {
  channel: string;
  type: "userData";
  customerId?: string;
  data: WSUserDataPushData;
}

/**
 * 是否为 wspush:all + userData 推送（与 agent 数组形态区分）
 */
export function isWSUserDataPushMessage(
  event: unknown,
): event is WSUserDataPushMessage {
  if (event === null || typeof event !== "object") {
    return false;
  }
  const e = event as Record<string, unknown>;
  if (e.channel !== WS_PUSH_ALL_CHANNEL || e.type !== "userData") {
    return false;
  }
  const data = e.data;
  if (data === null || typeof data !== "object" || Array.isArray(data)) {
    return false;
  }
  return true;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

// Preview for UserData push toast: NotificationCardContainer at spec.root (e.g. elements.container.props.title + .summary).
const extractTitleTextFromSpec = (
  spec: Spec,
): { title: string; text: string } | null => {
  const rootEl = spec.elements[spec.root];
  if (!isRecord(rootEl)) {
    return null;
  }
  const props = rootEl.props;
  if (!isRecord(props)) {
    return null;
  }
  const title = props.title;
  if (typeof title !== "string" || title.length === 0) {
    return null;
  }
  const summary = props.summary;
  const legacyText = props.text;
  const text =
    typeof summary === "string"
      ? summary
      : typeof legacyText === "string"
        ? legacyText
        : "";
  return { title, text };
};

// Legacy: body is JSON with elements.container (NotificationCardContainer), same props as dynamic_ui spec.
const extractLegacyContainerProps = (
  body: unknown,
): { title: string; text: string } | null => {
  if (!isRecord(body)) {
    return null;
  }
  const elements = body.elements;
  if (!isRecord(elements)) {
    return null;
  }
  const container = elements.container;
  if (!isRecord(container) || !isRecord(container.props)) {
    return null;
  }
  const title = container.props.title;
  if (typeof title !== "string") {
    return null;
  }
  const summary = container.props.summary;
  const inlineText = container.props.text;
  const text =
    typeof summary === "string"
      ? summary
      : typeof inlineText === "string"
        ? inlineText
        : "";
  return { title, text };
};

// 处理wspush:all / userData 消息
export const handleDescriptionText = (description: string) => {
  try {
    const data = JSON.parse(description) as unknown;
    if (!isRecord(data) || typeof data.body !== "string") {
      return null;
    }

    const bodyString = data.body;
    const spec = parseDynamicUiBody(bodyString);
    if (spec) {
      const fromSpec = extractTitleTextFromSpec(spec);
      if (fromSpec) {
        return fromSpec;
      }
    }

    try {
      const body = JSON.parse(bodyString) as unknown;
      if (isValidSpec(body)) {
        const fromSpec = extractTitleTextFromSpec(body);
        if (fromSpec) {
          return fromSpec;
        }
      }
      const legacy = extractLegacyContainerProps(body);
      if (legacy) {
        return legacy;
      }
    } catch {
      // ignore: body is not legacy JSON
    }

    return null;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    Track.log("error", "wsUserDataPush", "error", { cause: String(message) });
    return null;
  }
};
